import { useState } from "react"
import styled from "styled-components"

import { CheckmarkThinIcon } from "@minuthq/meatball-ui-icons"
import { Banner, Button, spacing, Text } from "@minuthq/meatball-ui-react"

import { NoCompletedTasks } from "src/components/EmptyState/NoCompletedTasks"
import { NoTodoTasks } from "src/components/EmptyState/NoTodoTasks"
import { Pager } from "src/components/Pager/Pager"
import { useUrlPager } from "src/components/Pager/useUrlPager"
import { DeleteTaskDialog } from "src/components/TaskManagement/DeleteTaskDialog"
import { EditTaskDialog } from "src/components/TaskManagement/EditTaskDialog"
import { TasksFilteredEmptyState } from "src/components/TaskManagement/TasksFilteredEmptyState"
import {
  usePostEditTaskInitiated,
  usePostTaskCompleted,
} from "src/data/analytics/queries/taskManagementAnalyticsQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import {
  useFetchTaskTableData,
  useTaskTypeLabels,
} from "src/data/taskManagement/hooks/useFetchTaskTableData"
import { convertAssigneeIdsToFilters } from "src/data/taskManagement/taskManagementFilters"
import { usePatchTask } from "src/data/taskManagement/taskManagementQueries"
import {
  TTask,
  TTaskFetchFilters,
} from "src/data/taskManagement/taskManagementTypes"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { GridTable } from "src/ui/GridTable/GridTable"
import { MoreButton } from "src/ui/GridTable/MoreButton"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import { MSkeleton } from "src/ui/MSkeleton/MSkeleton"
import { formatDate } from "src/utils/l10n"

import { AssigneeColumn } from "./AssigneeColumn"
import { TaskFilters } from "./TaskFilters"

export function TaskList({
  taskStatus,
  sortBy = "due_date",
  sortOrder = "asc",
}: {
  taskStatus: TTask["status"]
  sortBy?: TTaskFetchFilters["sort_by"]
  sortOrder?: TTaskFetchFilters["sort_order"]
}) {
  const { t, langKeys } = useTranslate()
  const { orgId } = useOrganization()

  const [selectedTask, setSelectedTask] = useState<TTask | null>(null)
  const [deleteTask, setDeleteTask] = useState<TTask | null>(null)

  const taskTypes = useTaskTypeLabels()

  const { clock_type } = useGetUser()

  const { limit, offset, setOffset } = useUrlPager({
    initialLimit: 15,
    initialOffset: 0,
    offsetKey: `offset_${taskStatus}`,
  })

  const [homeIds, setHomeIds] = useState<string[]>([])
  const [assigneeIds, setAssigneeIds] = useState<string[]>([])
  const [searchString, setSearchString] = useState<string>("")

  const homeIdsFilter = homeIds.length > 0 ? homeIds : undefined
  const assigneeIdsFilter = convertAssigneeIdsToFilters(assigneeIds)
  const searchFilter = searchString.length > 0 ? searchString : undefined

  const patchTask = usePatchTask()

  const postEditTaskInitiated = usePostEditTaskInitiated()
  const postTaskCompleted = usePostTaskCompleted()

  const { loading, tasks, homes, pagingTotalCount, error } =
    useFetchTaskTableData({
      statuses: [taskStatus],
      limit,
      offset,
      home_ids: homeIdsFilter,
      assigned_to: assigneeIdsFilter,
      title: searchFilter,
      sort_by: sortBy,
      sort_order: sortOrder,
    })

  const isFiltered =
    searchFilter || homeIdsFilter || assigneeIdsFilter.length > 0

  const hasNoTasksOnFilter = !tasks.length && !loading && isFiltered

  function handleEditTask(task: TTask) {
    setSelectedTask(task)

    postEditTaskInitiated.mutate({
      task_type: task.category,
    })
  }

  function handleMarkTaskAsComplete(task: TTask) {
    patchTask.mutate(
      {
        body: { status: "done" },
        path: { task_id: task.task_id, organization_id: orgId },
      },
      {
        onSuccess: () => {
          postTaskCompleted.mutate({
            task_type: task.category,
          })
        },
      }
    )
  }

  const columns: TableColumn<TTask>[] = [
    {
      value: "title",
      label: t(langKeys.task_management_task_title),
      enableSort: true,
      sortButtonSpacing: "compact",
      disableClickPropagation: true,
      renderCell: (data) => {
        return (
          <Text variant="subtitle">
            <StyledButton
              variant="minimal"
              onClick={() => handleEditTask(data)}
            >
              {data.title}
            </StyledButton>
          </Text>
        )
      },
      columnWidth: "auto",
    },
    {
      value: "task_type",
      label: t(langKeys.task_management_task_type),
      enableSort: true,
      sortButtonSpacing: "compact",
      renderCell: (data) => {
        return <Text variant="body">{taskTypes[data.category]}</Text>
      },
      columnWidth: "auto",
    },

    {
      value: "unit",
      label: t(langKeys.home),
      enableSort: true,
      sortButtonSpacing: "compact",
      renderCell: (data) => {
        return (
          <Text variant="body">
            {homes.find((h) => h.home_id === data.home_id)?.name ?? ""}
          </Text>
        )
      },
      columnWidth: "auto",
    },
    {
      value: "assign_to",
      label: t(langKeys.task_management_task_assign_to),
      enableSort: true,
      sortButtonSpacing: "compact",
      renderCell: (data) => {
        return (
          <AssigneeColumn
            assignee={data.assigned_to}
            taskId={data.task_id}
            orgId={orgId}
            homeId={data.home_id}
          />
        )
      },
      columnWidth: "auto",
    },

    {
      value: "due_date",
      label: t(langKeys.task_management_task_due_date),
      enableSort: true,
      sortButtonSpacing: "compact",
      renderCell: (data) => {
        return (
          <Text variant="body">
            {formatDate({
              date: data.time_window.due_date,
              clockType: clock_type,
            })}
          </Text>
        )
      },
      columnWidth: "auto",
    },
    {
      value: "more_button",
      label: "",
      renderCell: (data) => (
        <MoreButtonWrapper>
          <MoreButton
            onDelete={() => setDeleteTask(data)}
            deleteLabel={t(langKeys.delete)}
            onEdit={() => handleEditTask(data)}
            editLabel={t(langKeys.task_management_edit_task)}
            menuItems={[
              {
                contents: (
                  <CompleteTaskBox>
                    <CheckmarkThinIcon width={18} strokeWidth={2} />
                    {t(langKeys.task_management_mark_as_done_action_title)}
                  </CompleteTaskBox>
                ),
                key: "complete-task",
                onClick: () => handleMarkTaskAsComplete(data),
                hidden: data.status === "done",
              },
            ]}
          />
        </MoreButtonWrapper>
      ),
    },
  ]

  const { rows, headerElements, templateColumns } = useTableColumns<TTask>({
    columns,
    data: tasks,
  })

  if (loading) {
    return (
      <SkeletonWrapper>
        <MSkeleton variant="rect" height={30} />
        <MSkeleton variant="rect" height={30} />
      </SkeletonWrapper>
    )
  }

  if (error) {
    return (
      <Banner type="error">
        {t(langKeys.failed_general_error_try_refreshing_page)}
      </Banner>
    )
  }

  if (taskStatus === "todo" && tasks.length === 0 && !hasNoTasksOnFilter) {
    return <NoTodoTasks />
  }

  if (taskStatus === "done" && tasks.length === 0 && !hasNoTasksOnFilter) {
    return <NoCompletedTasks />
  }

  return (
    <>
      <TaskFilters
        homeIds={homeIds}
        setHomeIds={setHomeIds}
        assigneeIds={assigneeIds}
        setAssigneeIds={setAssigneeIds}
        searchString={searchString}
        setSearchString={setSearchString}
      />

      <GridTable
        header={headerElements}
        rows={rows}
        templateColumns={templateColumns}
        loading={loading}
        useFallbackResponsiveMode
      />

      {hasNoTasksOnFilter && <TasksFilteredEmptyState />}

      <Pager
        limit={limit}
        offset={offset}
        setOffset={setOffset}
        totalCount={pagingTotalCount}
      />

      {!!selectedTask && (
        <EditTaskDialog
          task={selectedTask}
          homes={homes}
          onClose={() => setSelectedTask(null)}
        />
      )}

      {deleteTask && (
        <DeleteTaskDialog
          task={deleteTask}
          onClose={() => setDeleteTask(null)}
        />
      )}
    </>
  )
}

const MoreButtonWrapper = styled.div`
  display: grid;
  place-items: center;
`

const StyledButton = styled(Button)`
  text-align: left;
`

const CompleteTaskBox = styled.div`
  display: flex;
  gap: ${spacing.XS};
`

const SkeletonWrapper = styled.div`
  display: grid;
  gap: ${spacing.XS};
`
